/**
 * Replaces route parameters in a route constant string
 * @param routeConstant - The route constant string (e.g., '/admin/locations/:id/strategy')
 * @param params - Object containing parameter key-value pairs to replace
 * @returns The route string with parameters replaced
 *
 * @example
 * const route = replaceRouteParams('/admin/locations/:id/strategy', { id: '123' })
 * // Returns: '/admin/locations/123/strategy'
 *
 * @example
 * const route = replaceRouteParams('/admin/users/:userId/posts/:postId', {
 *   userId: '456',
 *   postId: '789'
 * })
 * // Returns: '/admin/users/456/posts/789'
 */
export const replaceRouteParams = (
  routeConstant: string,
  params: Record<string, string | number>
): string => {
  let result = routeConstant;

  Object.entries(params).forEach(([key, value]) => {
    const paramKey = `:${key}`;
    if (result.includes(paramKey)) {
      result = result.replace(paramKey, value.toString());
    }
  });

  return result;
};

/**
 * Replaces a single route parameter in a route constant string
 * @param routeConstant - The route constant string
 * @param paramKey - The parameter key without the colon (e.g., 'id' not ':id')
 * @param paramValue - The value to replace the parameter with
 * @returns The route string with the parameter replaced
 *
 * @example
 * const route = replaceRouteParam('/admin/locations/:id/strategy', 'id', '123')
 * // Returns: '/admin/locations/123/strategy'
 */
export const replaceRouteParam = (
  routeConstant: string,
  paramKey: string,
  paramValue: string | number
): string => {
  const paramPattern = `:${paramKey}`;
  return routeConstant.replace(paramPattern, paramValue.toString());
};
